import { type CSSObject } from '@mui/material/styles'

import { styled } from '../../utils/styled'
import Typography from '../Typography'
import { type ActionCardVariant } from './types'
import { type Theme } from '../theme'
import Grid from '../Grid'
import Card from '../Card'

const getHeaderBackgroundColor = (theme: Theme, variant: ActionCardVariant): string => {
  const variantColors: Record<ActionCardVariant, string> = {
    'semantic-destructive': theme.tokens.background.semantic.error.main.default,
    'semantic-warning': theme.tokens.background.semantic.warning.main.default,
    'semantic-success': theme.tokens.background.semantic.success.main.default,
    'semantic-info': theme.tokens.background.semantic.info.main.default,
    'semantic-neutral': theme.tokens.background.tonal.medium.default
  }

  return variantColors[variant]
}

const getHeaderTitleColor = (theme: Theme, variant: ActionCardVariant): string => {
  if (variant === 'semantic-neutral') {
    return theme.tokens.foreground.main.primary
  }

  return theme.tokens.foreground.main.staticWhite
}

export const StyledActionCard = styled(Card)(
  ({ theme }): CSSObject => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    borderRadius: theme.borderRadius.xLarge,
    backgroundColor: theme.tokens.background.main.default,
    padding: '0',
    margin: '0'
  })
)

export const StyledActionCardHeader = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'variant'
})<{ variant: ActionCardVariant }>(
  ({ theme, variant }): CSSObject => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: '4px 20px',
    backgroundColor: getHeaderBackgroundColor(theme, variant)
  })
)

export const StyledActionCardTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'actionCardVariant'
})<{ actionCardVariant: ActionCardVariant }>(
  ({ theme, actionCardVariant }): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    color: getHeaderTitleColor(theme, actionCardVariant)
  })
)

export const StyledActionCardContent = styled(Grid)(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    width: '100%'
  })
)

export const StyledActionCardDescription = styled('div')(
  ({ theme }): CSSObject => ({
    color: theme.tokens.foreground.main.primary,
    ...theme.typography.body1
  })
)

export const StyledActionCardActions = styled(Grid)(
  (): CSSObject => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px'
  })
)
